import React from "react";

const H2 = ({ children }) => (
  <h2 className="font-serif-display text-base md:text-lg text-[var(--lc-ink)]">{children}</h2>
);
const P = ({ children, className = "" }) => (
  <p className={`mt-3 text-[15px] leading-relaxed text-[var(--lc-muted)] ${className}`}>{children}</p>
);
const Section = ({ id, children }) => (
  <section data-testid={`privacy-policy-section-${id}`} className="border-t border-[var(--lc-line)] pt-6">{children}</section>
);
const UL = ({ items }) => (
  <ul className="mt-3 space-y-2 list-disc pl-5 text-[15px] leading-relaxed text-[var(--lc-muted)] marker:text-[var(--lc-moss)]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);
const B = ({ children }) => <strong className="font-semibold text-[var(--lc-ink)]">{children}</strong>;

export default function PrivacyPolicyPage() {
  return (
    <main className="pt-[110px] pb-24 bg-[var(--lc-cream)]" data-testid="privacy-policy-page">
      <article className="mx-auto max-w-3xl px-5 lg:px-10">
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1]" data-testid="privacy-policy-title">
          Privacy Policy — Longev Circle
        </h1>
        <div className="mt-6 eyebrow text-[var(--lc-moss)]" data-testid="privacy-policy-updated"><B>Last updated: 19 October 2026</B></div>
        <P className="mt-8">
          <B>Controller:</B> Longev Circle, Basel, Switzerland.<br />
          <B>Contact for privacy questions:</B> via the contact/booking form on longevcircle.com.
        </P>
        <P>
          This policy covers the Longev Circle website (longevcircle.com) and the LONGEV app, together "our services". We are based in Basel, Switzerland: the Swiss Federal Act on Data Protection (nDSG) applies; where members in the EU/UK are concerned, the GDPR applies as well.
        </P>

        <div className="mt-12 space-y-10">
          <Section id="1">
            <H2>1. Data we collect</H2>
            <P><B>a) Contact and account data</B> — name, email, phone number, date of birth, and login credentials, provided when you book a consultation, build a custom plan, or create a member account.</P>
            <P><B>b) Longevity Assessment and health data</B> — your assessment answers (goals, lifestyle, nutrition, sleep), plus measured data collected in person or entered in the app: blood pressure, resting heart rate, body composition, grip strength, mobility/balance results, bloodwork/lab panel results, biological-age results, and any medical information you share with us. This is <em>sensitive personal data</em> under Swiss law — we process it only with your explicit consent (see §3).</P>
            <P><B>c) Coaching and progress data</B> — training, meal and recovery logs you enter in the app, session notes, coach feedback, and reminder/notification preferences.</P>
            <P><B>d) Payment data</B> — if you pay for services online, billing is processed by our payment service provider; we do not store full card numbers on our systems.</P>
            <P><B>e) Communications</B> — messages you send us (email, WhatsApp, in-app chat) and discovery-call booking details.</P>
            <P><B>f) Technical data</B> — device type, OS, app version, log/diagnostic data, and aggregated usage statistics.</P>
            <P><B>g) Testimonials</B> — published only with your written consent; you may ask us to remove them at any time.</P>
            <P>We do not knowingly collect data from children under 16.</P>
          </Section>

          <Section id="2">
            <H2>2. How we use your data</H2>
            <UL items={[
              "To provide coaching, plans, assessments and app functionality.",
              "Physician review of assessments and lab work (with your explicit consent).",
              "Scheduling, reminders and member communications.",
              "Billing and accounting (legal obligation — 10 years under Swiss law).",
              "Improving our services and securing them (legitimate interest).",
              "Marketing only with your opt-in; unsubscribe anytime.",
            ]} />
          </Section>

          <Section id="3">
            <H2>3. Your consent for health data</H2>
            <P>Because we handle health data, we ask for your <B>explicit consent</B> before you submit the Longevity Assessment and before any lab work is ordered. You can withdraw consent at any time (see §7); withdrawal does not affect processing already carried out. The reviewing physician is bound by medical confidentiality.</P>
          </Section>

          <Section id="4">
            <H2>4. Who we share data with</H2>
            <P>We do not sell personal data. Sharing is limited to:</P>
            <UL items={[
              "Our coaching team and the licensed physician / laboratory partners who review assessments — all bound by confidentiality.",
              "IT and hosting providers who operate our website and app infrastructure (currently Emergent).",
              "Payment processing providers, where applicable.",
              "Authorities where legally required.",
            ]} />
          </Section>

          <Section id="5">
            <H2>5. International transfers</H2>
            <P>Your data is stored on our hosting provider's servers. Where data is transferred outside Switzerland or the EU/EEA, we ensure appropriate safeguards are in place.</P>
          </Section>

          <Section id="6">
            <H2>6. How long we keep data</H2>
            <P>We keep personal data only as long as necessary for the purposes above: for the duration of your membership and a reasonable period afterwards, longer where the law requires it (billing records: 10 years under Swiss law). Marketing consents are kept until you unsubscribe.</P>
          </Section>

          <Section id="7">
            <H2>7. Your rights</H2>
            <P>You may at any time request <B>access, correction, deletion, or portability</B> of your data, <B>object to or restrict</B> processing, and withdraw consent. Contact us via the booking form on longevcircle.com — we respond within 30 days. You may also complain to the Swiss Federal Data Protection and Information Commissioner (FDPIC), or, for EU members, your local supervisory authority.</P>
          </Section>

          <Section id="8">
            <H2>8. Security</H2>
            <P>Encryption in transit and at rest; access limited to staff who need it. No method is 100% secure; we will notify you of breaches as required by law.</P>
          </Section>

          <Section id="9">
            <H2>9. Cookies</H2>
            <P>The website uses technically necessary cookies (for example, for security and remembering your settings).</P>
          </Section>

          <Section id="10">
            <H2>10. Changes</H2>
            <P>We will post updates on this page and, for material changes, notify members in-app or by email.</P>
          </Section>
        </div>

        <hr className="mt-12 border-[var(--lc-line)]" />
        <P className="mt-6 italic"><em>Privacy questions: contact us via the booking form on longevcircle.com.</em></P>
      </article>
    </main>
  );
}
